import type { ComponentType } from 'react';
import { CalendarDays, CircleDot, Flag, Pencil, Tag, UserRound } from 'lucide-react';
import { ActivityFeed } from '@/components/activity/ActivityFeed';
import type { ActivityEntry } from '@/types/activity';
import { TaskGlyph } from '../TaskGlyph';
import { taskActivityActorName, taskActivityEntry } from './taskActivityCopy';

/** The mark a filing wears, which is the board's own. */
const CREATED_ICON = TaskGlyph;

/**
 * The mark beside a change, by which field it was about.
 *
 * Grouped rather than one per field: a reader scanning the rail is looking for
 * "a person changed" or "a date moved", not for which of the two dates it was.
 * Kept apart from the mark a filing wears, because an event and a field are two
 * different things to be named by.
 */
const FIELD_ICONS: Record<string, ComponentType<{ className?: string }>> = {
  status: CircleDot,
  priority: Flag,
  title: Pencil,
  description: Pencil,
  assignee_user_id: UserRound,
  reviewer_user_id: UserRound,
  start_date: CalendarDays,
  due_date: CalendarDays,
  labels: Tag,
};

/**
 * A save that changed several fields wears the plain edit mark, because no one
 * of them is what it was about — and so does a field this build has no mark for.
 */
const iconFor = (entry: ActivityEntry) => {
  if (entry.type === 'created') {
    return CREATED_ICON;
  }

  return (entry.field ? FIELD_ICONS[entry.field] : undefined) ?? Pencil;
};

/**
 * What has happened to a task.
 *
 * Everything a timeline shares with every other module lives in
 * `ActivityFeed`; this supplies the two things that are the task board's own —
 * what a change to one of its fields says, and which mark it wears.
 */
export const TaskActivityPanel = ({ taskId }: { taskId: string }) => (
  <ActivityFeed
    entityType="task"
    entityId={taskId}
    entityLabel="task"
    actorName={taskActivityActorName}
    entryFor={taskActivityEntry}
    iconFor={iconFor}
  />
);
